import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Phone, CheckCircle } from 'lucide-react';
import { servicesData } from '../data/servicesData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const service = servicesData[serviceSlug];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/" className="text-cyan-600 hover:text-cyan-700">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-cyan-600 via-cyan-500 to-cyan-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/#services"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={18} />
            <span>Back to Services</span>
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            {service.title}
          </h1>
          <p className="text-xl text-white/90 leading-relaxed">
            {service.subtitle}
          </p>

          {service.isEmergency && (
            <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <p className="text-white text-lg mb-4">
                For dental emergencies, call <strong>(916) 773-1122</strong>. Please listen to the entire voicemail message.
              </p>
              <a
                href="tel:9167731122"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-6 py-3 rounded-lg transition-all"
              >
                <Phone size={20} />
                <span>Call (916) 773-1122</span>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {service.sections.map((section, index) => (
            <div key={index} className="mb-12">
              {section.title && (
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {section.title}
                </h2>
              )}
              
              {section.content && (
                <p className="text-gray-600 leading-relaxed mb-4">
                  {section.content}
                </p>
              )}

              {section.footer && (
                <p className="text-gray-600 leading-relaxed mb-4">
                  {section.footer}
                </p>
              )}

              {/* Benefits list */}
              {section.benefits && Array.isArray(section.benefits) && (
                <ul className="space-y-3 mb-6">
                  {section.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="text-cyan-600 mt-0.5 flex-shrink-0" size={20} />
                      {typeof benefit === 'string' ? (
                        <span className="text-gray-700">{benefit}</span>
                      ) : (
                        <div>
                          <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                          <p className="text-gray-600 text-sm mt-1">{benefit.content}</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {/* Simple list */}
              {section.list && (
                <ul className="space-y-3 mb-6">
                  {section.list.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="text-cyan-600 mt-0.5 flex-shrink-0" size={20} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Treatments grid */}
              {section.treatments && (
                <div className="grid md:grid-cols-2 gap-6 mt-6">
                  {section.treatments.map((treatment, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                      <h4 className="font-bold text-gray-900 mb-2">{treatment.title}</h4>
                      <p className="text-gray-600 text-sm">{treatment.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Subsections */}
              {section.subsections && (
                <div className="space-y-6 mt-6">
                  {section.subsections.map((sub, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                      {sub.title && (
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">
                          {sub.title}
                        </h3>
                      )}
                      {sub.content && (
                        <p className="text-gray-600 leading-relaxed">
                          {sub.content}
                        </p>
                      )}
                      {sub.benefits && (
                        <div className="space-y-4 mt-4">
                          {sub.benefits.map((benefit, j) => (
                            <div key={j}>
                              <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                              <p className="text-gray-600 text-sm mt-1">{benefit.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl p-8 md:p-10 text-center border border-cyan-200">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              {service.cta.title}
            </h3>
            <p className="text-gray-600 mb-6">
              {service.cta.subtitle}
            </p>
            {service.cta.note && (
              <p className="text-amber-600 font-medium mb-4 text-sm">
                {service.cta.note}
              </p>
            )}
            <a
              href="tel:9167731122"
              className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-8 py-4 rounded-lg transition-all shadow-lg hover:shadow-xl"
            >
              <Phone size={20} />
              <span>Call (916) 773-1122</span>
            </a>
          </div>

          {/* Back to Services Link */}
          <div className="mt-12 text-center">
            <Link
              to="/#services"
              className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold transition-colors"
            >
              <ArrowLeft size={18} />
              <span>Back to All Services</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;
